var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { create } from "zustand";
import { sendAjaxRequest } from "../API";
export const useMessageStore = create()((set, get) => ({
    conversations: [],
    messages: {},
    selectedConversationId: null,
    loading: false,
    error: null,
    setSelectedConversation: (conversationId) => {
        set({ selectedConversationId: conversationId });
    },
    loadConversations: (auth, logger) => __awaiter(void 0, void 0, void 0, function* () {
        if (!logger)
            return;
        const LOGPREFIX = "MessageStore.loadConversations: ";
        set({ loading: true, error: null });
        try {
            logger.log(LOGPREFIX + "Loading conversations for user: " + auth.userId);
            const payload = {
                operation: "listConversations",
                sequence: auth.sequence,
            };
            return new Promise((resolve, reject) => {
                sendAjaxRequest((ok, status, message) => {
                    try {
                        logger.log(LOGPREFIX +
                            "Server response - ok: " +
                            ok +
                            ", status: " +
                            status +
                            ", message: " +
                            message);
                        if (status >= 200 && status < 300) {
                            const response = JSON.parse(message);
                            if (response.bearer && response.sequence) {
                                auth.setBearerAndSequence(response.bearer, response.sequence, logger);
                            }
                            const conversations = response.conversations || [];
                            set({
                                conversations: conversations,
                                loading: false,
                            });
                            logger.log(LOGPREFIX +
                                "Successfully loaded " +
                                conversations.length +
                                " conversations");
                            resolve();
                        }
                        else {
                            let errorMessage = "Failed to load conversations";
                            try {
                                const errorResponse = JSON.parse(message);
                                errorMessage = errorResponse.error || errorMessage;
                            }
                            catch (parseError) {
                                logger.error("" + parseError);
                                errorMessage = message || errorMessage;
                            }
                            throw new Error(errorMessage);
                        }
                    }
                    catch (error) {
                        logger.log(LOGPREFIX + "Exception: " + error);
                        set({
                            error: "Failed to load conversations",
                            loading: false,
                        });
                        reject(error);
                    }
                }, "conversations", payload, auth.bearer, { initializeCacheBuster: () => { }, incrementCacheBuster: () => { } }, logger);
            });
        }
        catch (error) {
            logger.log(LOGPREFIX + "Exception: " + error);
            set({
                error: "Failed to load conversations",
                loading: false,
            });
        }
    }),
    loadMessages: (conversationId, auth, logger) => __awaiter(void 0, void 0, void 0, function* () {
        if (!logger)
            return;
        const LOGPREFIX = "MessageStore.loadMessages: ";
        set({ loading: true, error: null });
        try {
            logger.log(LOGPREFIX + "Loading messages for conversation: " + conversationId);
            // TODO: Implement actual API call
            const currentMessages = get().messages;
            set({
                messages: Object.assign(Object.assign({}, currentMessages), { [conversationId]: [] }),
                loading: false,
            });
            logger.log(LOGPREFIX + "Successfully loaded 0 messages");
        }
        catch (error) {
            logger.log(LOGPREFIX + "Exception: " + error);
            set({
                error: "Failed to load messages",
                loading: false,
            });
        }
    }),
    sendMessage: (conversationId, content, messageType, auth, logger) => __awaiter(void 0, void 0, void 0, function* () {
        if (!logger)
            return;
        const LOGPREFIX = "MessageStore.sendMessage: ";
        try {
            logger.log(LOGPREFIX + "Sending message to conversation: " + conversationId);
            // TODO: Implement actual API call
            logger.log(LOGPREFIX + "Successfully sent message");
        }
        catch (error) {
            logger.log(LOGPREFIX + "Exception: " + error);
            set({
                error: "Failed to send message",
            });
        }
    }),
    createConversation: (participantUserIds, auth, cacheBusterStore, logger) => __awaiter(void 0, void 0, void 0, function* () {
        if (!logger)
            throw new Error("Logger is required");
        const LOGPREFIX = "MessageStore.createConversation: ";
        set({ loading: true, error: null });
        try {
            logger.log(LOGPREFIX +
                "Creating conversation with participants: " +
                participantUserIds.join(", "));
            const payload = {
                operation: "createConversation",
                participantUserIds: participantUserIds,
                sequence: auth.sequence,
            };
            return new Promise((resolve, reject) => {
                sendAjaxRequest((ok, status, message) => {
                    try {
                        logger.log(LOGPREFIX +
                            "Server response - ok: " +
                            ok +
                            ", status: " +
                            status +
                            ", message: " +
                            message);
                        // Check for successful status codes (200-299) regardless of 'ok' parameter
                        if (status >= 200 && status < 300) {
                            const response = JSON.parse(message);
                            if (response.bearer && response.sequence) {
                                auth.setBearerAndSequence(response.bearer, response.sequence, logger);
                            }
                            if (response.conversations &&
                                response.conversations.length > 0) {
                                const newConversation = response.conversations[0];
                                // Add to conversations list
                                const currentConversations = get().conversations;
                                set({
                                    conversations: [newConversation, ...currentConversations],
                                    loading: false,
                                });
                                logger.log(LOGPREFIX +
                                    "Successfully created conversation: " +
                                    newConversation.conversationId);
                                resolve(newConversation.conversationId);
                            }
                            else {
                                throw new Error("No conversations returned from server");
                            }
                        }
                        else {
                            // Handle error response
                            let errorMessage = "Failed to create conversation";
                            try {
                                const errorResponse = JSON.parse(message);
                                errorMessage = errorResponse.error || errorMessage;
                            }
                            catch (parseError) {
                                logger.error("" + parseError);
                                // If JSON parsing fails, use the raw message or default error
                                errorMessage = message || errorMessage;
                            }
                            throw new Error(errorMessage);
                        }
                    }
                    catch (error) {
                        logger.log(LOGPREFIX + "Exception: " + error);
                        set({
                            error: "Failed to create conversation",
                            loading: false,
                        });
                        reject(error);
                    }
                }, "conversations", payload, auth.bearer, cacheBusterStore, logger);
            });
        }
        catch (error) {
            logger.log(LOGPREFIX + "Exception: " + error);
            set({
                error: "Failed to create conversation",
                loading: false,
            });
            throw error;
        }
    }),
    deleteConversation: (conversationId, auth, cacheBusterStore, logger) => __awaiter(void 0, void 0, void 0, function* () {
        if (!logger)
            throw new Error("Logger is required");
        const LOGPREFIX = "MessageStore.deleteConversation: ";
        set({ loading: true, error: null });
        try {
            logger.log(LOGPREFIX + "Deleting conversation: " + conversationId);
            const payload = {
                operation: "deleteConversation",
                conversationId: conversationId,
                sequence: auth.sequence,
            };
            return new Promise((resolve, reject) => {
                sendAjaxRequest((ok, status, message) => {
                    try {
                        logger.log(LOGPREFIX +
                            "Server response - ok: " +
                            ok +
                            ", status: " +
                            status +
                            ", message: " +
                            message);
                        if (status >= 200 && status < 300) {
                            const response = JSON.parse(message);
                            if (response.bearer && response.sequence) {
                                auth.setBearerAndSequence(response.bearer, response.sequence, logger);
                            }
                            // Remove conversation from list
                            const currentConversations = get().conversations;
                            const currentMessages = get().messages;
                            const remainingMessages = Object.fromEntries(Object.entries(currentMessages).filter(([key]) => key !== conversationId));
                            set({
                                conversations: currentConversations.filter((c) => c.conversationId !== conversationId),
                                messages: remainingMessages,
                                selectedConversationId: get().selectedConversationId === conversationId
                                    ? null
                                    : get().selectedConversationId,
                                loading: false,
                            });
                            logger.log(LOGPREFIX +
                                "Successfully deleted conversation: " +
                                conversationId);
                            resolve();
                        }
                        else {
                            let errorMessage = "Failed to delete conversation";
                            try {
                                const errorResponse = JSON.parse(message);
                                errorMessage = errorResponse.error || errorMessage;
                            }
                            catch (parseError) {
                                logger.error("" + parseError);
                                errorMessage = message || errorMessage;
                            }
                            throw new Error(errorMessage);
                        }
                    }
                    catch (error) {
                        logger.log(LOGPREFIX + "Exception: " + error);
                        set({
                            error: "Failed to delete conversation",
                            loading: false,
                        });
                        reject(error);
                    }
                }, "conversations", payload, auth.bearer, cacheBusterStore, logger);
            });
        }
        catch (error) {
            logger.log(LOGPREFIX + "Exception: " + error);
            set({
                error: "Failed to delete conversation",
                loading: false,
            });
            throw error;
        }
    }),
    reset: () => {
        set({
            conversations: [],
            messages: {},
            selectedConversationId: null,
            loading: false,
            error: null,
        });
    },
}));
